// src/contexts/WalletContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { initializeWeb3 } from '../utils/contractUtils';

export const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [web3, setWeb3] = useState(null);
  const [contract, setContract] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Initialize web3 and check if wallet is already connected
  useEffect(() => {
    const checkConnection = async () => {
      // Skip reconnection if explicitly disconnected
      if (localStorage.getItem('WALLET_DISCONNECTED') === 'true') {
        console.log("Skipping auto-connection due to previous disconnect");
        return;
      }
      
      if (window.ethereum) {
        try {
          console.log("Checking for existing connection...");
          const { web3: web3Instance, contract: contractInstance } = initializeWeb3();
          setWeb3(web3Instance);
          setContract(contractInstance);
          
          // Check if already connected
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          console.log("Found accounts:", accounts);
          
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            updateBalance(accounts[0], web3Instance);
          }
        } catch (err) {
          console.error('Failed to initialize wallet connection:', err);
          setError('Failed to connect to wallet');
        }
      }
    };

    checkConnection();
  }, []);

  // Update account balance
  const updateBalance = useCallback(async (accountAddress, web3Instance) => {
    if (!accountAddress || !web3Instance) return;
    
    try {
      const balance = await web3Instance.eth.getBalance(accountAddress);
      const balanceInEther = web3Instance.utils.fromWei(balance, 'ether');
      setBalance(parseFloat(balanceInEther).toFixed(4));
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    }
  }, []);

  // Connect wallet
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('Please install MetaMask or another Ethereum-compatible wallet');
      return;
    }

    setIsConnecting(true);
    setError(null);
    
    // Clear disconnected flag
    localStorage.removeItem('WALLET_DISCONNECTED');

    try {
      console.log("Initializing Web3...");
      const { web3: web3Instance, contract: contractInstance } = initializeWeb3();
      console.log("Web3 initialized");
      
      setWeb3(web3Instance);
      setContract(contractInstance);
      
      console.log("Requesting accounts...");
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      console.log("Accounts received:", accounts);
      
      if (accounts && accounts.length > 0) {
        setAccount(accounts[0]);
        updateBalance(accounts[0], web3Instance);
      } else {
        setError('No accounts found or user rejected the connection');
      }
    } catch (err) {
      console.error('Failed to connect wallet:', err);
      setError('Failed to connect wallet: ' + (err.message || 'Unknown error'));
    } finally {
      setIsConnecting(false);
    }
  };

// Disconnect wallet - wrapped in useCallback
  const disconnectWallet = useCallback(() => {
    setAccount(null);
    setBalance(null);
    setWeb3(null);
    setContract(null);
    
    // Store disconnected state to prevent auto-reconnect
    localStorage.setItem('WALLET_DISCONNECTED', 'true');
    
    console.log("Wallet disconnected");
  }, []);

  // Listen for account changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          // User disconnected their wallet
          disconnectWallet();
        } else if (accounts[0] !== account) {
          // User switched accounts
          setAccount(accounts[0]);
          updateBalance(accounts[0], web3);
        }
      };

      const handleChainChanged = () => {
        // Reload the page when the chain changes
        window.location.reload();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      // Cleanup
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [account, web3, updateBalance, disconnectWallet]);

  // Refresh balance
  const refreshBalance = useCallback(() => {
    if (account && web3) {
      updateBalance(account, web3);
    }
  }, [account, web3, updateBalance]);

  return (
    <WalletContext.Provider
      value={{
        account,
        balance,
        web3,
        contract,
        isConnecting,
        error,
        connectWallet,
        disconnectWallet,
        refreshBalance
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};